import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'

import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  TextField,
  Typography,
  Link,
  InputLabel,
} from '@mui/material'
import { ReactComponent as Warning } from '../../../assets/icons/warning.svg'

import useTranslations from '../../../i18n/useTranslations'
import { getErrorProp, getFieldProps } from '../../../common/formProps'
import { makeFormPropTypes } from '../../../common/formikPropTypes'
import { shippingSend3ShipmentPropTypes } from '../../../common/propTypes'

import PackageFormUI from '../../../ui/modules/packageForm'
import PackageRow from '../../../ui/modules/packageForm/PackageRow'
import Autocomplete from '../../../ui/components/autocomplete'

import FormLayout from './FormLayout'
import useStyles from './styles'
import TooltipButton from '../../../ui/components/tooltipButton'
import ServiceRadioButtons from '../../../ui/modules/ServiceRadioButtons'
import Loader from '../Loader'

const checkPackageHasContent = packageContent =>
  (packageContent.weight !== null) |
  (packageContent.length !== null) |
  (packageContent.width !== null) |
  (packageContent.height !== null)

/**
 * Check if the given package has trash icon.
 * The index is neccessary because only the first element can have or not icon
 *
 * @param { Number } packageIndex
 * @param { Object } packageContent
 * @returns { Boolean }
 */
const checkPackageHasTrashIcon = (packageIndex, packageContent) =>
  packageIndex === 0 ? checkPackageHasContent(packageContent) : true

const Step3Shipment = ({
  values,
  originOrDestinAddressChanged,
  originAddressChanged,
  fieldErrors,
  formErrors,
  requiredMeasures,
  touched,
  onChange,
  onBlur,
  onPackageRowBlur,
  onPackageRowFocus,
  serviceTypeOptions,
  isLoadingServiceTypeOptions,
  isErrorServiceTypes,
  onAddPackage,
  isAddButtonDisabled,
  onRemovePackage,
  totalWeight,
  packageErrors,
  hasCustoms,
  isToCanaryIslands,
  imputationCenter,
  isOriginPudo,
  international,
  setMerchandise,
}) => {
  const t = useTranslations()
  const [selectedBtn, setSelectedBtn] = useState(1)
  const classes = useStyles()
  const [firstRender, setFirstRender] = useState(true)

  useEffect(() => {
    if (values?.serviceType && !firstRender) {
      values.packages.map((packageContent, index) =>
        onPackageRowBlur('', index),
      )
    } else if (values?.serviceType) setFirstRender(false)
  }, [values?.serviceType])

  useEffect(() => {
    if (values?.serviceType) {
      const updatedServiceType = serviceTypeOptions.find(
        option => option.value === values.serviceType.value,
      )
      if (updatedServiceType) {
        onChange({ target: { name: 'serviceType', value: updatedServiceType } })
      } else {
        onChange({ target: { name: 'serviceType', value: '' } })
      }
    }
  }, [serviceTypeOptions])

  const allFieldsComplete = () => {
    if (!values || !values.packages || values.packages.length === 0) {
      return false
    }
    return values.packages.every(
      ({ height, length, weight, width }) =>
        height !== null && length !== null && weight !== null && width !== null,
    )
  }

  return (
    <FormLayout>
      <>
        {originOrDestinAddressChanged ? (
          <Alert
            iconMapping={{
              warning: <Warning />,
            }}
            severity="warning"
            className={classes.tooltipCenter}
          >
            <Typography variant="h3" sx={{ marginBottom: 1 }}>
              {t('shipping-send-form:shipment:address-change-detected')}
            </Typography>
            <Typography>
              {t(
                originAddressChanged
                  ? 'shipping-send-form:shipment:origin-address-changed'
                  : 'shipping-send-form:shipment:destin-address-changed',
              )}
            </Typography>
          </Alert>
        ) : null}
        {hasCustoms ? (
          <>
            <Box component="section" className={classes.section}>
              <Typography
                variant="h2"
                className={clsx('decorator', classes.title)}
              >
                {t('shipping-send-form:shipment:content-type')}
              </Typography>
              <ButtonGroup className={classes.buttonGroup}>
                <Button
                  variant={selectedBtn === 1 ? 'contained' : 'outlined'}
                  onClick={() => {
                    setSelectedBtn(1)
                    setMerchandise(true)
                  }}
                >
                  {t('shipping-send-form:shipment:merchandise')}
                </Button>
                <Button
                  variant={selectedBtn === 2 ? 'contained' : 'outlined'}
                  onClick={() => {
                    setSelectedBtn(2)
                    setMerchandise(false)
                  }}
                >
                  {t('shipping-send-form:shipment:documents')}
                </Button>
              </ButtonGroup>
            </Box>
          </>
        ) : null}
        <Box component="section" className={classes.section}>
          <Typography
            variant="h2"
            className={clsx('secondaryTitle', 'decorator', classes.title)}
          >
            {t('shipping-send-form:shipment:packages')}
            <TooltipButton
              type="primary"
              title={t('tooltip:measures-price')}
              arrow
              placement="top"
            />
          </Typography>

          <Typography className={classes.paragraphStatement}>
            {t('shipping-send-form:shipment:packages:content')}
          </Typography>

          <PackageFormUI
            count={values.packages.length}
            weight={totalWeight}
            onAdd={onAddPackage}
            isAddButtonDisabled={
              isAddButtonDisabled ||
              values?.serviceType?.originalCode === 'CIEC'
            }
          >
            {values.packages.map((packageContent, index) => (
              <PackageRow
                // we need a specific key here, otherwise it triggers `onChange`
                // on the inputs when adding/removing rows, and Formik goes crazy
                //
                // key is generated randomly (but kept across renders) on the app
                // side
                key={packageContent.key}
                onFocus={e => onPackageRowFocus(e, index)}
                index={index}
                values={packageContent}
                requiredMeasures={requiredMeasures}
                touched={touched.packages?.[index] ?? {}}
                fieldErrors={fieldErrors.packages?.[index] ?? {}}
                formErrors={formErrors}
                isLastRow={index === values.packages.length - 1}
                onDelete={() => onRemovePackage(index)}
                onChange={onChange}
                onBlur={e => onPackageRowBlur(e, index)}
                hasTrashIcon={checkPackageHasTrashIcon(index, packageContent)}
                packageError={packageErrors[index] ?? null}
              />
            ))}
          </PackageFormUI>
          {isOriginPudo ? (
            <Alert
              severity="warning"
              className={classes.alertShippingRestrictions}
            >
              {t('shipping-send-form:one-package-alert')}
            </Alert>
          ) : null}
        </Box>

        <Box component="section" className={classes.section}>
          <Typography
            variant="h2"
            className={clsx('secondaryTitle', 'decorator', classes.title)}
          >
            {t('shipping-send-form:shipment:service-type')}
            {packageErrors?.length === 0 && allFieldsComplete() ? (
              <TooltipButton
                type="primary"
                title={t('tooltip:service-price')}
                arrow
                placement="top"
              />
            ) : null}
          </Typography>
          {isLoadingServiceTypeOptions ? (
            <Loader />
          ) : !values.packages.some(checkPackageHasContent) ? (
            <Typography
              className={classes.paragraphStatementWithoutMarginBottom}
            >
              {t('shipping-send-form:shipment:fill-packages')}
            </Typography>
          ) : serviceTypeOptions?.length > 0 ? (
            <ServiceRadioButtons
              id="serviceType"
              serviceTypeOptions={
                values.packages.length > 1
                  ? serviceTypeOptions.filter(
                      service => service.originalCode !== 'CIEC',
                    )
                  : serviceTypeOptions
              }
              selectedService={values.serviceType}
              onChangeService={value => {
                onChange({ target: { name: 'serviceType', value } })
              }}
              {...getFieldProps('serviceType', values, fieldErrors, touched, t)}
              shouldShowPrice={
                packageErrors?.length === 0 && allFieldsComplete()
              }
              error={getErrorProp('serviceType', fieldErrors, touched)}
            />
          ) : (
            <Box>
              <Alert
                iconMapping={{
                  warning: <Warning />,
                }}
                severity="warning"
                className={classes.tooltipCenter}
              >
                <Typography>
                  {isErrorServiceTypes
                    ? t('alert:environment-error')
                    : t('alert:no-products')}
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>

        {values.packages.some(checkPackageHasContent) &&
        selectedBtn === 1 &&
        (isToCanaryIslands || hasCustoms) ? (
          <Box component="section" className={classes.section}>
            <>
              <Typography
                variant="h2"
                className={clsx('secondaryTitle', 'decorator', classes.title)}
              >
                {t('shipping-send-form:shipment:custom')}
              </Typography>
              <Typography
                className={classes.paragraph}
                component="p"
                variant="body2"
              >
                {t('shipping-send-form:shipment:custom-charge')}
              </Typography>
              <Autocomplete
                options={
                  [
                    { label: imputationCenter, value: 'CLIENT' },
                    { label: t('noun:recipientName'), value: 'RECIPIENT' },
                  ] ?? []
                }
                value={values.customsSelected}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                disableClearable
                name="customsSelected"
                onChange={(_, selected) => {
                  onChange({
                    target: { name: 'customsSelected', value: selected },
                  })
                }}
                getOptionLabel={option => option.label.toUpperCase() || ''}
                className={'input-large'}
              />
            </>
          </Box>
        ) : null}

        <Box component="section" className={classes.section}>
          <Typography
            variant="h2"
            className={clsx('secondaryTitle', 'decorator', classes.title)}
          >
            {t('shipping-send-form:shipment:reference-number')}
          </Typography>

          <TextField
            name="referenceNumber"
            label={
              <>
                {t('noun:clientReference')}
                <TooltipButton
                  className={classes.tooltipInput}
                  type="primary"
                  title={t('tooltip:reference-number')}
                  placement="right"
                />
              </>
            }
            {...getFieldProps(
              'referenceNumber',
              values,
              fieldErrors,
              touched,
              t,
            )}
            onChange={onChange}
            onBlur={onBlur}
            className="input-medium"
          />
        </Box>

        {hasCustoms && selectedBtn === 2 ? (
          <Box component="section" className={classes.sectionKeepInMind}>
            <Typography
              variant="h2"
              className={clsx('secondaryTitle', 'decorator', classes.title)}
            >
              {t('shipping-send-form:keep-in-mind')}
            </Typography>

            <Alert severity="warning" className={classes.alert}>
              <Typography variant="label" component="p">
                {t('shipping-send-form:sending-documents', {
                  strong: text => <strong>{text}</strong>,
                })}
              </Typography>
            </Alert>
          </Box>
        ) : null}
      </>
    </FormLayout>
  )
}

Step3Shipment.propTypes = {
  ...makeFormPropTypes(shippingSend3ShipmentPropTypes),

  originOrDestinAddressChanged: PropTypes.bool,
  originAddressChanged: PropTypes.bool,

  onPackageRowBlur: PropTypes.func,
  onPackageRowFocus: PropTypes.func,
  openCustomsPdf: PropTypes.func,
  openRequiredDocsPdf: PropTypes.func,
  openInventorySheetPdf: PropTypes.func,
  isAddButtonDisabled: PropTypes.bool,

  // Service types
  serviceTypeOptions: PropTypes.array,

  // Package list
  onAddPackage: PropTypes.func.isRequired,
  onRemovePackage: PropTypes.func.isRequired,
  totalWeight: PropTypes.number,

  // Package errors
  packageErrors: PropTypes.array,
  hasCustoms: PropTypes.bool,
  isToCanaryIslands: PropTypes.bool,
  imputationCenter: PropTypes.string,
}

export default Step3Shipment
